import { AgDialog, AutocompleteGenericEntity, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@mui/material';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import React, { ChangeEventHandler, useState } from 'react';

import { useRedisWritesBlocked } from '@/apis/call';
import { checkSearches } from '@/apis/searches';
import {
  DUPLICATE_SEARCH_ERROR_MSG,
  SAVE_SEARCH_DESCRIPTION_MAX_LEN,
  SAVE_SEARCH_NAME_MAX_LEN,
} from '@/constants';
import { ApiBodyError, isDefinedError } from '@/errors';
import { FilterCriteria } from '@/filterCriteria/types';
import { isTemplatePlaceholder } from '@/types';
import { SearchMetadata } from '@/types/shared';
import { SearchTreeNode } from '@/types/searchTreeTypes';
import { walkTreeAndRemoveEphemeralIds } from '@/util/tree';
import UsersFetchAutocomplete from '@components/UsersFetchAutocomplete';
import { searchTemplateDataAtom } from '@/state/searchTemplate/searchTemplateData';
import { useSetAtom } from 'jotai';
import { useSaveSearch } from './hooks';

export type Search =
  | FilterCriteria
  | SearchTreeNode
  // For typing reasons, should never be undefined when dialog is open
  | undefined;

/**
 * Type guard: true when `value` is a SearchTreeNode (advanced search tree),
 * false when it is FilterCriteria (quick search) or undefined.
 */
export function isSearchTreeNode(value: Search): value is SearchTreeNode {
  return value != null && typeof value === 'object' && 'op' in value;
}

export type { SearchMetadata };

type SaveSearchDialogPropsBase = {
  dialogStatus: 'open' | 'closed' | 'prefilled';
  onClose: (searchMetadata?: SearchMetadata) => void;
  currentSearchMetadata: SearchMetadata;
};

export type SaveSearchDialogProps =
  | (SaveSearchDialogPropsBase & { searchType: 'Quick'; search: FilterCriteria })
  | (SaveSearchDialogPropsBase & {
      searchType: 'Advanced';
      search: SearchTreeNode | undefined;
    });

const SaveSearchDialog = ({
  dialogStatus,
  onClose,
  search,
  currentSearchMetadata,
  searchType,
}: SaveSearchDialogProps) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { enqueueErrorNotification } = useSaladBar();
  const writesBlocked = useRedisWritesBlocked();

  const setSearchTemplateData = useSetAtom(searchTemplateDataAtom);

  const [searchName, setSearchName] = useState(
    dialogStatus === 'prefilled' ? (currentSearchMetadata.name ?? '') : ''
  );
  const [searchDescription, setSearchDescription] = useState(
    dialogStatus === 'prefilled' ? (currentSearchMetadata.description ?? '') : ''
  );

  const [toggleValue, setToggleValue] = useState<'me' | 'individual'>(
    dialogStatus === 'prefilled' && currentSearchMetadata.id ? 'individual' : 'me'
  );
  const [userAddEntities, setUserAddEntities] = useState<AutocompleteGenericEntity[]>([]);

  const [isDuplicate, setIsDuplicate] = useState(false);

  const { mutateAsync: saveSearch, error, isError, reset: resetSave } = useSaveSearch();

  if (dialogStatus !== 'closed' && searchType === 'Advanced' && search === undefined) {
    throw new Error('Search tree is undefined when dialog is open');
  }

  const handleSave = async () => {
    if (writesBlocked) return;
    if (searchType === 'Advanced' && search === undefined) {
      throw new Error('Search tree is undefined when dialog is open');
    }
    const userSub = user?.sub || '';
    const assignedToSubs =
      userAddEntities.length > 0 ? userAddEntities.map((x) => String(x.id)) : [userSub];

    // Strip out ephemeral ids (_leafNodeId, _internalNodeId) from the search tree if needed
    const searchJson =
      searchType === 'Advanced'
        ? JSON.stringify(walkTreeAndRemoveEphemeralIds(search))
        : JSON.stringify(search);

    try {
      const result = await saveSearch({
        name: searchName,
        description: searchDescription,
        assignedToSubs,
        searchJson,
        searchType,
        savedBySub: userSub,
      });
      setSearchTemplateData(undefined);
      const searchForUser = result?.find(({ assignedToSub }) => assignedToSub === userSub);

      onClose({
        name: searchName,
        description: searchDescription,
        id: searchForUser?.id,
      });
    } catch {
      enqueueErrorNotification('The search could not be saved.');
    }
  };

  const getErrorText = () => {
    if (isDuplicate) return DUPLICATE_SEARCH_ERROR_MSG;
    if (isError && error instanceof ApiBodyError) {
      if (isDefinedError(error)) {
        const { message, name } = error.body.errors[0];
        if (name === 'CreateSearchDuplicateNameSavedBySubError') return DUPLICATE_SEARCH_ERROR_MSG;
        return message;
      }
      return 'There was an issue with the backend.';
    }
    return '';
  };

  const errorText: string = getErrorText();

  const handleSearchNameChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    resetSave();
    setIsDuplicate(false);
    setSearchName(e.target.value);
  };

  const handleSearchDescriptionChange: ChangeEventHandler<HTMLInputElement> = (e) =>
    setSearchDescription(e.target.value);
  //

  const checkNameClashOnBlurOrEnter = async (
    toggle = toggleValue,
    userEntities = userAddEntities
  ) => {
    if (!searchName) return;
    const token = await getAccessTokenSilently();
    const assignedToSubs =
      toggle === 'individual' ? userEntities.map((x) => String(x.id)) : [user?.sub ?? ''];

    const checkSearchResult =
      toggle === 'individual' && assignedToSubs.length === 0
        ? false
        : await checkSearches(token, { name: searchName, assignedToSubs });

    setIsDuplicate(checkSearchResult);
  };

  const handleNameBlur = async () => {
    await checkNameClashOnBlurOrEnter();
  };

  const handleNameKeyPress = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      await checkNameClashOnBlurOrEnter();
    }
  };

  return (
    <AgDialog
      isOpen={dialogStatus !== 'closed'}
      onClose={() => onClose(undefined)}
      dialogTitle="Save search"
      primaryButton={{
        text: 'Save',
        onClick: handleSave,
        disabled:
          writesBlocked ||
          searchName.length < 3 ||
          isDuplicate ||
          (toggleValue === 'individual' &&
            (userAddEntities.length < 1 || searchDescription.length < 3)),
      }}
      data-testid="SaveSearchDialog"
    >
      <TextField
        margin="dense"
        id="name"
        label={'Search name (min. 3 characters)'}
        type="text"
        fullWidth
        variant="standard"
        value={searchName}
        onChange={handleSearchNameChange}
        error={isError || isDuplicate}
        helperText={errorText}
        slotProps={{
          htmlInput: {
            maxLength: SAVE_SEARCH_NAME_MAX_LEN,
          },
        }}
        onBlur={handleNameBlur}
        onKeyUp={handleNameKeyPress}
      />
      <TextField
        margin="dense"
        id="description"
        label={'Search description'}
        type="text"
        fullWidth
        variant="filled"
        value={searchDescription}
        onChange={handleSearchDescriptionChange}
        multiline
        minRows={2}
        error={
          toggleValue === 'individual' &&
          searchDescription?.length === 0 &&
          userAddEntities.length > 0
        }
        helperText={
          toggleValue === 'individual' &&
          searchDescription?.length < 3 &&
          userAddEntities.length > 0
            ? 'Search description of at least 3 characters required when sharing with other users'
            : ''
        }
        slotProps={{
          htmlInput: {
            maxLength: SAVE_SEARCH_DESCRIPTION_MAX_LEN,
          },
        }}
      />
      <Stack
        spacing={2}
        sx={{
          alignItems: 'center',
          mb: 0,
          mt: 2
        }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={toggleValue}
          onChange={async (_e, value) => {
            if (value) {
              setToggleValue(value);
              resetSave();
              await checkNameClashOnBlurOrEnter(value);
            }
          }}
        >
          <ToggleButton sx={{ width: '140px' }} value="me">
            Me
          </ToggleButton>
          <ToggleButton sx={{ width: '140px' }} value="individual">
            Individual
          </ToggleButton>
        </ToggleButtonGroup>
        {toggleValue === 'individual' && (
          <Box sx={{
            width: "100%"
          }}>
            <UsersFetchAutocomplete
              label={'Search for users'}
              value={userAddEntities}
              onChange={async (value) => {
                const valueCorrectType = isTemplatePlaceholder(value) ? [] : value;
                setUserAddEntities(valueCorrectType);
                await checkNameClashOnBlurOrEnter(toggleValue, valueCorrectType);
              }}
              dataTestidPrefix={'saveSearchUsers'}
            />
          </Box>
        )}
      </Stack>
    </AgDialog>
  );
};

export default SaveSearchDialog;
